import React, { useEffect, useState } from 'react';
import moment from 'moment/moment.js';
import { Grid, TableRowDetail, TableTreeColumn } from '@devexpress/dx-react-grid-material-ui';
import {
  CustomTreeData,
  DataTypeProvider,
  RowDetailState,
  SelectionState,
  TreeDataState,
} from '@devexpress/dx-react-grid';
import useTable from 'shared/hooks/customHooks/useTable';
import TableWrapper from 'shared/components/tables/TableWrapper';
import { CustomFormatterWithTooltipAndComponent } from 'shared/components/andtComponents/TableComponents/TableComponents';
import { COST_CENTERS_COLUMNS, COST_CENTERS_DATA_ACCESS_COLUMNS, getColumns } from '../../consts';
import { useCostCentersContext } from './../contexts/costCentersContext';
import CostCentersNoData from 'users/containers/Organization/components/EmptyStates/CostCentersNoData';
import CostCentersIcon from 'users/containers/Organization/CostCenters/CostCentersIcon';
import CostCenterActions from 'users/containers/Organization/CostCenters/CostCenterActions';
import CostCenterAccountsData from 'users/containers/Organization/CostCenters/CostCenterAccountsData';
import CostCenterAssignedBy from 'users/containers/Organization/CostCenters/CostCenterAssignedBy';
import {
  CustomTreeCheckboxCell,
  RowContextProvider,
} from 'users/containers/Organization/CostCenters/CustomTreeCheckboxCell';
import {
  getChildRows,
  getCostCenterAccountsCounter,
} from 'users/containers/Organization/CostCenters/costCentersHelperFunctions';
import CreateEditCostCenter from 'users/containers/Organization/CostCenters/costCentersModals/CreateEditCostCenter/CreateEditCostCenter';
import EditCostCenterAccounts from 'users/containers/Organization/CostCenters/costCentersModals/CreateEditCostCenter/EditCostCenterAccounts';
import { GenerateIcon, ICONS } from '@pileus-cloud/anodot-frontend-common';
import CostCenterUnassignedNameWrapper from 'users/containers/Organization/CostCenters/CostCentersViews/Unassigned/CostCenterUnassignedNameWrapper';
import UnassignedAccounts from 'users/containers/Organization/CostCenters/CostCentersViews/Unassigned/UnassignedAccounts';
import classnames from 'classnames';

import styles from './../CostCenterTable.module.scss';

const CostCentersTreeView = ({
  accountsData,
  costCenters,
  isCreateEditMode,
  isSelectionEnabled,
  isRoleDataAccessView = false,
  onSelectionChange,
  noCostCentersInOrg,
}) => {
  const { NewTableRow, CustomToggleCell } = useTable();

  const [expandedRowIds, setExpandedRowIds] = useState([]);
  const [selectedCostCenters, setSelectedCostCenters] = useState([]);
  const [expandedTreeRowIds, setExpandedTreeRowIds] = useState([]);

  function handleExpandedTreeRowIdsChange(rowIds) {
    setExpandedTreeRowIds(rowIds);
  }

  const { isAllExpanded, isCustomersView, selectedCustomer, setIsAllExpanded } = useCostCentersContext();

  const [tableColumnExtensions] = useState([
    { columnName: COST_CENTERS_COLUMNS.COST_CENTER_NAME.columnName },
    { columnName: COST_CENTERS_COLUMNS.CREATED_AT.columnName, width: '170px' },
    { columnName: COST_CENTERS_COLUMNS.CREATE_SUB_COST_CENTER.columnName, width: '60px' },
    { columnName: COST_CENTERS_COLUMNS.ADD_LINKED_ACCOUNTS.columnName, width: '60px' },
    { columnName: COST_CENTERS_COLUMNS.ACTIONS.columnName, width: '40px' },
    { columnName: COST_CENTERS_DATA_ACCESS_COLUMNS.ASSIGNED_BY.columnName, width: '300px' },
    { columnName: COST_CENTERS_DATA_ACCESS_COLUMNS.REMOVE.columnName, width: '80px' },
  ]);

  useEffect(() => {
    if (!isAllExpanded) {
      setExpandedRowIds([]);
    } else {
      setExpandedRowIds(costCenters?.map((row, index) => index));
    }
  }, [costCenters, isAllExpanded]);

  useEffect(() => {
    if (expandedRowIds?.length === 0) {
      setIsAllExpanded(false);
    }
  }, [expandedRowIds, setIsAllExpanded]);

  function isUnassignedRow(row) {
    return row?.id === 'unassigned';
  }

  const CostCenterNameWrapper = (props) => {
    const { row } = props;

    return isUnassignedRow(row) ? (
      <CostCenterUnassignedNameWrapper {...props} />
    ) : (
      <div className={styles.costCenterNameAndId}>
        <CustomFormatterWithTooltipAndComponent {...props} isLeftComponent>
          {!isRoleDataAccessView ? <CostCentersIcon /> : null}
        </CustomFormatterWithTooltipAndComponent>
        <div className={styles.costCenterId}>{props?.row?.externalId ? `Code: ${props.row.externalId}` : ''}</div>
        <div className={styles.costCenterCount}>
          (sub accounts: {getCostCenterAccountsCounter(props, accountsData)})
        </div>
      </div>
    );
  };

  const AssignedByWrapper = ({ row }) => {
    if (row) {
      return <CostCenterAssignedBy costCenter={row} />;
    }
    return null;
  };

  const CostCenterActionsWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    const subTreeCostCenters = getChildRows(row, costCenters);
    return <CostCenterActions costCenter={row} subTreeCostCenters={subTreeCostCenters} />;
  };

  const CreateSubCostCenterWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    return <CreateEditCostCenter parentCcId={row.id} />;
  };

  const CreatedAtWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }

    return (
      <div className={styles.createdAt}>
        <GenerateIcon iconName={ICONS.calendar.name} />
        Created: {row?.creationTime ? moment(row?.creationTime).format('MMM DD YYYY') : null}
      </div>
    );
  };

  const CostCenterAccountsDataWrapper = (props) => {
    const { row } = props;
    return isUnassignedRow(row) ? (
      <UnassignedAccounts unassignedAccounts={row.unassignedAccounts} />
    ) : (
      <CostCenterAccountsData
        {...props}
        isSelectionEnabled={isSelectionEnabled}
        isAccountsViewOnly={isRoleDataAccessView}
        isCreateEditMode={isCreateEditMode && !isRoleDataAccessView}
      />
    );
  };

  const EditCostCenterWrapper = ({ row }) => {
    if (isUnassignedRow(row)) {
      return null;
    }
    return <EditCostCenterAccounts costCenter={row} parentCcId={row.id} />;
  };

  const CostCentersNoDataWrapper = (props) => (
    <CostCentersNoData
      {...props}
      noCostCentersInOrg={noCostCentersInOrg}
      mainText={isCustomersView && !selectedCustomer?.name ? 'Customer not selected' : null}
      subTexts={
        isCustomersView && !selectedCustomer?.name ? ['Please select Customer to view their Cost Centers'] : null
      }
      isRoleDataAccessView={isRoleDataAccessView}
    />
  );

  return (
    <div className={styles.costCentersGrid}>
      <Grid
        rows={costCenters}
        columns={getColumns(isRoleDataAccessView ? COST_CENTERS_DATA_ACCESS_COLUMNS : COST_CENTERS_COLUMNS)}
        automationid="cost-centers-list"
      >
        {isRoleDataAccessView && isSelectionEnabled && (
          <SelectionState selection={selectedCostCenters} onSelectionChange={setSelectedCostCenters} />
        )}
        <TreeDataState expandedRowIds={expandedTreeRowIds} onExpandedRowIdsChange={handleExpandedTreeRowIdsChange} />
        <CustomTreeData getChildRows={getChildRows} />
        <RowDetailState expandedRowIds={expandedRowIds} onExpandedRowIdsChange={setExpandedRowIds} />
        <DataTypeProvider
          for={[
            COST_CENTERS_COLUMNS.COST_CENTER_NAME.columnName,
            COST_CENTERS_DATA_ACCESS_COLUMNS.COST_CENTER_NAME.columnName,
          ]}
          formatterComponent={CostCenterNameWrapper}
        />
        {!isRoleDataAccessView ? (
          <DataTypeProvider for={[COST_CENTERS_COLUMNS.CREATED_AT.columnName]} formatterComponent={CreatedAtWrapper} />
        ) : null}
        <DataTypeProvider
          for={[COST_CENTERS_COLUMNS.CREATE_SUB_COST_CENTER.columnName]}
          formatterComponent={CreateSubCostCenterWrapper}
        />
        {!isRoleDataAccessView ? (
          <DataTypeProvider
            for={[COST_CENTERS_COLUMNS.ACTIONS.columnName]}
            formatterComponent={CostCenterActionsWrapper}
          />
        ) : null}
        {!isRoleDataAccessView ? (
          <DataTypeProvider
            for={[COST_CENTERS_COLUMNS.ADD_LINKED_ACCOUNTS.columnName]}
            formatterComponent={EditCostCenterWrapper}
          />
        ) : null}
        {isRoleDataAccessView ? (
          <DataTypeProvider
            for={[COST_CENTERS_DATA_ACCESS_COLUMNS.ASSIGNED_BY.columnName]}
            formatterComponent={AssignedByWrapper}
          />
        ) : null}
        <TableWrapper
          noDataCellComponent={CostCentersNoDataWrapper}
          rowComponent={(props) =>
            isUnassignedRow(props.row) ? (
              <NewTableRow
                expandedRowIds={expandedRowIds}
                setExpandedRowIds={setExpandedRowIds}
                className={classnames(styles.unassignedAccountsRow, {
                  [styles.emptyUnassigned]: !props.row.unassignedAccounts?.length,
                })}
                {...props}
              />
            ) : (
              <NewTableRow expandedRowIds={expandedRowIds} setExpandedRowIds={setExpandedRowIds} {...props} />
            )
          }
          height="auto"
          columnExtensions={tableColumnExtensions}
        />
        <TableTreeColumn
          cellComponent={({ row, ...restProps }) => (
            <RowContextProvider row={row}>
              <TableTreeColumn.Cell {...restProps} />
            </RowContextProvider>
          )}
          checkboxComponent={(props) => <CustomTreeCheckboxCell {...props} onToggle={onSelectionChange} />}
          for={COST_CENTERS_COLUMNS.COST_CENTER_NAME.columnName}
          showSelectionControls={isSelectionEnabled && isRoleDataAccessView}
        />
        <TableRowDetail contentComponent={CostCenterAccountsDataWrapper} toggleCellComponent={CustomToggleCell} />
      </Grid>
    </div>
  );
};


export default CostCentersTreeView;
